import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { BackendType, FrontendType, ObjectType } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import { convertVoucherToFrontend, Voucher } from '../model/voucher.types';

export type GetVouchersSearchParams = {
    code?: string;
    page?: number;
    size?: number;
};

export const convertGetVouchersSearchParamsToBackend = (
    params: GetVouchersSearchParams
): GetVouchersSearchParams => {
    return params;
};

export type GetVouchersResponse<T extends ObjectType = FrontendType> = {
    content: Voucher<T>[];
    size: number;
    totalElements: number;
    totalPages: number;
};

const voucherUrls = {
    vouchers: urlBuilder('order', 'sold-vouchers'),
};

/**
 * Type definition for the API with injected voucher hooks
 */
export interface VoucherApiHooks {
    useGetVouchersQuery: (params: GetVouchersSearchParams) => {
        data?: GetVouchersResponse;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePostVoucherRevokeInsuranceMutation: () => [
        (params: { voucherId: string }) => Promise<unknown>,
        { isLoading: boolean; error?: unknown },
    ];
    usePostVoucherCancelInsuranceMutation: () => [
        (params: { voucherId: string }) => Promise<unknown>,
        { isLoading: boolean; error?: unknown },
    ];
    usePostVoucherCancelInsuranceFromInsuranceMutation: () => [
        (params: { voucherId: string }) => Promise<unknown>,
        { isLoading: boolean; error?: unknown },
    ];
}

/**
 * Voucher endpoint definitions
 */
export const voucherEndpoints = (builder: EntityEndpointBuilder) => ({
    getVouchers: builder.query<GetVouchersResponse, GetVouchersSearchParams>({
        query: (params: GetVouchersSearchParams) => {
            return `${voucherUrls.vouchers}${buildSearchParams(
                convertGetVouchersSearchParamsToBackend(params)
            )}`;
        },
        transformResponse: (
            response: GetVouchersResponse<BackendType>
        ): GetVouchersResponse => {
            return {
                ...response,
                content: response.content.map(convertVoucherToFrontend),
            };
        },
        providesTags: ['VOUCHERS'],
    }),

    postVoucherCancelInsurance: builder.mutation<
        unknown,
        { voucherId: string }
    >({
        query: ({ voucherId }: { voucherId: string }) => ({
            url: `${voucherUrls.vouchers}/${voucherId}/cancel-insurance`,
            method: 'POST',
            body: { voucherId },
        }),
        invalidatesTags: ['VOUCHERS'],
    }),

    postVoucherRevokeInsurance: builder.mutation<
        unknown,
        { voucherId: string }
    >({
        query: ({ voucherId }: { voucherId: string }) => ({
            url: `${voucherUrls.vouchers}/${voucherId}/revoke-insurance`,
            method: 'POST',
            body: { voucherId },
        }),
        invalidatesTags: ['VOUCHERS'],
    }),

    postVoucherCancelInsuranceFromInsurance: builder.mutation<
        unknown,
        { voucherId: string }
    >({
        query: ({ voucherId }: { voucherId: string }) => ({
            url: `${voucherUrls.vouchers}/${voucherId}/embargo-insurance`,
            method: 'POST',
            body: { voucherId },
        }),
        invalidatesTags: ['VOUCHERS'],
    }),
});

export const voucherApi = createEntityApi({
    tagTypes: ['VOUCHERS'],
    endpoints: voucherEndpoints,
});

// Export hooks with explicit types for TypeScript inference
export const useGetVouchersQuery: VoucherApiHooks['useGetVouchersQuery'] =
    voucherApi.useGetVouchersQuery;
export const usePostVoucherRevokeInsuranceMutation: VoucherApiHooks['usePostVoucherRevokeInsuranceMutation'] =
    voucherApi.usePostVoucherRevokeInsuranceMutation;
export const usePostVoucherCancelInsuranceMutation: VoucherApiHooks['usePostVoucherCancelInsuranceMutation'] =
    voucherApi.usePostVoucherCancelInsuranceMutation;
export const usePostVoucherCancelInsuranceFromInsuranceMutation: VoucherApiHooks['usePostVoucherCancelInsuranceFromInsuranceMutation'] =
    voucherApi.usePostVoucherCancelInsuranceFromInsuranceMutation;
